import { Box, useMediaQuery, useTheme } from "@mui/system";
import React, { useEffect, useRef, useState } from "react";
import Legend from "../../Legend";
import { CurveType, LineChart as MUILineChart } from "@mui/x-charts";
import { formatNumber } from "../../../utils/formatNumber";
import AreaGradient from "./LineChartAreaGradient";
import { Typography } from "@mui/material";

interface LineChartProps {
    parentRef: React.RefObject<HTMLDivElement>;
    title?: string;
    colorPalette?: string[];
    topBottomPadding?: number;
    leftRightPadding?: number;
    data: {
        xAxis: {
            data: string[] | number[];
            scaleType?: 'linear' | 'log' | 'time' | 'band' | 'point' | 'pow' | 'sqrt' | 'utc' | undefined;
        };
        series: {
            id: string;
            label: string;
            data: (number | null)[];
            curve?: CurveType;
            showMark?: boolean;
            area?: boolean;
            dashed?: boolean;
            valueFormatter?: (value: number | null, context: { dataIndex: number }) => string | null;
        }[];
        legendItems?: {
            color: string;
            dashed?: boolean;
            label: string;
        }[];
    };
    chartInitialSize?: {
        width?: number;
        height?: number;
    };
};

const LineChart = ({
    parentRef,
    colorPalette,
    data,
    chartInitialSize,
    title,
    topBottomPadding = 0,
    leftRightPadding = 0,
}: LineChartProps) => {
    const titleRef = useRef<HTMLDivElement>(null);
    const legendRef = useRef<HTMLDivElement>(null);
    const theme = useTheme();
    const isTabletOrMobile = useMediaQuery(theme.breakpoints.down('md'));
    const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
    const [lineChartWidth, setLineChartWidth] = useState(chartInitialSize?.width || 800);
    const [lineChartHeight, setLineChartHeight] = useState(chartInitialSize?.height || 400);

    // adjust line chart width/height based on parent container and the added Legend space
    useEffect(() => {
        const handleResize = () => {
            if (parentRef.current) {
                const parentWidth = parentRef.current.offsetWidth - leftRightPadding;
                const parentHeight = parentRef.current.offsetHeight - topBottomPadding;
                // On mobile/tablet: take almost full width minus a small padding.
                // On larger screens: take the combined width previously used by two pie charts.
                if (isTabletOrMobile) {
                    setLineChartWidth(Math.max(parentWidth - 32, 300));
                    const legendHeight = 56; // approx height of the custom Legend on mobile
                    const verticalPadding = 24; // space inside container
                    const computed = parentHeight ? parentHeight - legendHeight - verticalPadding - topBottomPadding : 300;
                    setLineChartHeight(Math.max(Math.min(computed, 420), 220));
                } else {
                    setLineChartWidth(Math.max(parentWidth, 600));
                    const legendHeight = legendRef.current ? legendRef.current.offsetHeight : 0; // approx height of the custom Legend on desktop
                    const titleHeight = titleRef.current ? titleRef.current.offsetHeight + 8 : 0;
                    const verticalPadding = 24;
                    const legendSize = data.legendItems?.length ? legendHeight + verticalPadding : 0;
                    const computed = parentHeight ? parentHeight - legendSize - titleHeight - topBottomPadding : 380;
                    setLineChartHeight(Math.max(computed, 300));
                }
            }
        };

        window.addEventListener("resize", handleResize);
        handleResize();

        return () => {
            window.removeEventListener("resize", handleResize);
            setLineChartWidth(chartInitialSize?.width || 800);
            setLineChartHeight(chartInitialSize?.height || 400);
        };
    }, [
        parentRef.current?.offsetWidth,
        parentRef.current?.offsetHeight,
        isTabletOrMobile,
        chartInitialSize?.height,
        chartInitialSize?.width,
        data.legendItems?.length,
        parentRef,
        titleRef.current?.offsetHeight,
        legendRef.current?.offsetHeight,
        topBottomPadding,
        leftRightPadding,
    ]);

    return (
        <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" height="100%" width="100%">
            {title &&
                <Typography align="center" variant="h6" mb={1} ref={titleRef}>{title}</Typography>
            }
            {data.legendItems?.length && (
                <Box mb={1.5} mt={1.5} ref={legendRef}>
                    <Legend items={data.legendItems} />
                </Box>
            )}
            <Box
                sx={{
                    width: lineChartWidth,
                    height: lineChartHeight,
                }}
            >
                <MUILineChart
                    colors={colorPalette}
                    xAxis={[
                        {
                            data: data.xAxis.data,
                            scaleType: data.xAxis.scaleType || 'point',
                        },
                    ]}
                    yAxis={[{ min: 0 }]}
                    series={data.series.map((seriesData) => (
                        {
                            id: seriesData.id,
                            label: seriesData.label,
                            showMark: seriesData.showMark || false,
                            curve: seriesData.curve || 'linear',
                            area: seriesData.area || false,
                            data: seriesData.data,
                            valueFormatter: seriesData.valueFormatter ? (value, context) => seriesData.valueFormatter!(value, context) : value => formatNumber(value || 0),
                        }
                    ))}
                    margin={isTabletOrMobile
                        ? { left: 50, right: isTablet ? 30 : 10, top: 20, bottom: 30 }
                        : { left: 60, right: 60, top: 20, bottom: 20 }
                    }
                    grid={{ horizontal: true, vertical: true }}
                    slotProps={{
                        legend: {
                            hidden: true,
                        },
                    }}
                    sx={{
                        ...data.series.reduce((acc, seriesData) => ({
                            ...acc,
                            [`& .MuiAreaElement-series-${seriesData.id}`]: {
                                fill: `url('#${seriesData.id}')`,
                            },
                            ...(seriesData.dashed ? {
                                [`& .MuiLineElement-series-${seriesData.id}`]: {
                                    strokeDasharray: '8 5',
                                },
                            } : {}),
                        }), {}),
                    }}

                >
                    {data.series.map((seriesData, index) => (
                        <AreaGradient
                            key={seriesData.id}
                            color={colorPalette?.[index] || theme.palette.primary.main}
                            id={seriesData.id}
                        />
                    ))}
                </MUILineChart>
            </Box>
        </Box>
    );
};

export default LineChart;
